"use client";

import { Menu, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { NAV_ANCORAS, NAV_PAGINAS } from "@/lib/nav";

import { Logo } from "./logo";

/**
 * Barra do topo, fixa sobre a faixa azul-marinho — a mesma assinatura visual do
 * site anterior. A navegação é por âncoras porque o site é uma página única.
 *
 * O menu mobile usa `<details>` nativo em vez de estado React: no celular, o
 * toque abre e fecha o menu mesmo antes da hidratação — ou se o JavaScript
 * falhar. Os links fecham o `<details>` depois do clique, mas a navegação por
 * âncora continua sendo a do próprio navegador.
 */
export function Header({
  siteName,
  logoUrl,
}: {
  siteName: string;
  logoUrl?: string | null;
}) {
  const pathname = usePathname();
  const naHome = pathname === "/";

  const hrefAncora = (id: string) => (naHome ? `#${id}` : `/#${id}`);

  const fecharMenu = (evento: React.MouseEvent<HTMLAnchorElement>) => {
    evento.currentTarget.closest("details")?.removeAttribute("open");
  };

  const linkTopo =
    "whitespace-nowrap font-mono text-xs uppercase tracking-widest2 text-paper/70 transition-colors duration-300 hover:text-gold-light";
  const linkMenu = "py-3 text-sm text-paper/70 transition-colors hover:text-paper";

  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-gradient-to-b from-navy-dark to-navy backdrop-blur-md">
      <div className="container-page flex items-center justify-between gap-4 py-4">
        <Logo siteName={siteName} logoUrl={logoUrl} className="min-w-0" />

        <nav
          aria-label="Navegação principal"
          className="hidden items-center gap-5 lg:flex"
        >
          {NAV_ANCORAS.map((item) => (
            <a key={item.id} href={hrefAncora(item.id)} className={linkTopo}>
              {item.curto}
            </a>
          ))}

          {NAV_PAGINAS.map((item) => (
            <Link key={item.href} href={item.href} className={linkTopo}>
              {item.rotulo}
            </Link>
          ))}
        </nav>

        <details key={pathname} className="shrink-0 lg:hidden">
          <summary
            aria-controls="menu-mobile"
            className="flex size-10 cursor-pointer list-none items-center justify-center rounded-lg text-paper/70 transition-colors hover:bg-white/10 hover:text-paper focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-light [&::-webkit-details-marker]:hidden"
          >
            <span className="sr-only">Menu</span>
            <Menu className="menu-fechado h-6 w-6" aria-hidden />
            <X className="menu-aberto hidden h-6 w-6" aria-hidden />
          </summary>

          <div
            id="menu-mobile"
            className="animate-slide-down absolute inset-x-0 top-full border-t border-navy-light/40 bg-gradient-to-b from-navy-dark to-navy"
            onClick={(evento) => {
              if ((evento.target as HTMLElement).closest("a")) {
                evento.currentTarget.closest("details")?.removeAttribute("open");
              }
            }}
          >
            <nav
              aria-label="Navegação principal (móvel)"
              className="container-page flex flex-col py-4"
            >
              {NAV_ANCORAS.map((item) => (
                <a
                  key={item.id}
                  href={hrefAncora(item.id)}
                  onClick={fecharMenu}
                  className={linkMenu}
                >
                  {item.label}
                </a>
              ))}

              {NAV_PAGINAS.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={fecharMenu}
                  className={linkMenu}
                >
                  {item.rotulo}
                </Link>
              ))}
            </nav>
          </div>
        </details>
      </div>
    </header>
  );
}
